import { motion } from "framer-motion";
import { FiInstagram, FiMapPin } from "react-icons/fi";
import { usePosts } from "../hooks/usePosts";
import styles from "./About.module.css";

export default function About() {
  const { posts } = usePosts();
  const countries = Array.from(new Set(posts.map((p) => p.country)));

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroGrid}>
          <motion.div
            className={styles.heroContent}
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className={styles.label}>About</span>
            <h1 className={styles.title}>
              Hi, I'm <span className={styles.accent}>Avara</span>
            </h1>
            <p className={styles.bio}>
              A passionate traveler, storyteller, and the person behind{" "}
              <a
                href="https://instagram.com/Travelwithavara"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                @TravelWithAvara
              </a>
              . I believe that every place has a story waiting to be told, and
              every journey changes us in ways we never expected.
            </p>
            <p className={styles.bio}>
              This blog is my corner of the internet where I share the
              experiences, emotions, and lessons that come with exploring our
              beautiful world. From the misty temples of Kyoto to the vibrant
              medinas of Marrakech, each story is written from the heart.
            </p>
            <p className={styles.bio}>
              When I'm not on the road, I'm planning my next adventure,
              editing photos, or dreaming about places I haven't been yet.
              Travel isn't just what I do — it's who I am.
            </p>

            <div className={styles.socialLinks}>
              <a
                href="https://instagram.com/Travelwithavara"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialBtn}
              >
                <FiInstagram size={18} /> Follow on Instagram
              </a>
            </div>
          </motion.div>

          <motion.div
            className={styles.heroVisual}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className={styles.imageGrid}>
              {posts.slice(0, 4).map((post, i) => (
                <motion.div
                  key={post.id}
                  className={styles.gridImage}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }}
                >
                  <img src={post.coverImage} alt={post.location} />
                  <div className={styles.imageLabel}>
                    <FiMapPin size={10} /> {post.location}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className={styles.places}>
        <div className={styles.container}>
          <motion.h2
            className={styles.placesTitle}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Places I've Been
          </motion.h2>
          <div className={styles.countriesGrid}>
            {countries.map((country, i) => (
              <motion.div
                key={country}
                className={styles.countryCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <span className={styles.countryName}>{country}</span>
                <span className={styles.countryCount}>
                  {posts.filter((p) => p.country === country).length}{" "}
                  {posts.filter((p) => p.country === country).length === 1
                    ? "story"
                    : "stories"}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.cta}>
        <div className={styles.container}>
          <motion.div
            className={styles.ctaCard}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className={styles.ctaTitle}>Let's Connect</h2>
            <p className={styles.ctaText}>
              Follow my journey on Instagram for daily travel content,
              behind-the-scenes moments, and real-time updates from wherever
              I am in the world.
            </p>
            <a
              href="https://instagram.com/Travelwithavara"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaBtn}
            >
              <FiInstagram size={18} /> @TravelWithAvara
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
